import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";

export function MfaChallenge({ challenge, onComplete }) {
    const { mfaVerify } = useAuth();
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [codes, setCodes] = useState(null);
    const [finalData, setFinalData] = useState(null);
    const [recoveryMode, setRecoveryMode] = useState(false);
    const setup = challenge.setup;

    const submit = async (e) => {
        e.preventDefault();
        toast.dismiss();
        setBusy(true);
        try {
            const data = await mfaVerify(challenge.pending_token, code.trim());
            if (data.recovery_codes) { setCodes(data.recovery_codes); setFinalData(data); }
            else onComplete(data);
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (codes) {
        return (
            <div className="bg-white border border-border rounded-xl p-6 space-y-4" data-testid="mfa-recovery-codes">
                <div className="flex items-center gap-2 text-brand-emerald font-semibold"><KeyRound className="h-5 w-5 text-brand-teal" /> Save your recovery codes</div>
                <p className="text-sm text-muted-foreground">Store these somewhere safe. Each code works once if you ever lose access to your authenticator app.</p>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-secondary/40 rounded-lg p-4">
                    {codes.map((c) => <span key={c} data-testid="recovery-code">{c}</span>)}
                </div>
                <Button className="w-full bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" onClick={() => onComplete(finalData)} data-testid="recovery-continue">
                    I've saved them — continue
                </Button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="bg-white border border-border rounded-xl p-6 space-y-4" data-testid="mfa-challenge">
            <div className="flex items-center gap-2 text-brand-emerald font-semibold">
                <ShieldCheck className="h-5 w-5 text-brand-teal" /> {setup ? "Set up 2-step verification" : "Two-step verification"}
            </div>
            {setup ? (
                <>
                    <p className="text-sm text-muted-foreground">Scan this QR code with Google Authenticator, Authy or 1Password, then enter the 6-digit code to finish enrolling.</p>
                    <div className="flex justify-center"><img src={challenge.qr_data_url} alt="Authenticator QR code" className="h-44 w-44 rounded-lg border border-border" data-testid="mfa-qr" /></div>
                    <div className="text-center text-[11px] text-muted-foreground">Can't scan? Enter this key manually:<br /><span className="font-mono text-brand-emerald break-all" data-testid="mfa-secret">{challenge.secret}</span></div>
                </>
            ) : (
                <p className="text-sm text-muted-foreground">{recoveryMode ? "Enter one of your one-time backup recovery codes." : "Enter the 6-digit code from your authenticator app."}</p>
            )}
            <div>
                <Label className="text-xs">{recoveryMode ? "Recovery code" : "6-digit code"}</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode={recoveryMode ? "text" : "numeric"} autoComplete="one-time-code" placeholder={recoveryMode ? "xxxx-xxxx" : "123456"} className="mt-1 text-center tracking-widest font-mono" data-testid="mfa-code-input" autoFocus />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="mfa-verify-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : setup ? "Verify & enable" : "Verify"}
            </Button>
            {!setup && (
                <button type="button" onClick={() => { setRecoveryMode((v) => !v); setCode(""); }} className="w-full text-center text-xs text-brand-teal hover:underline" data-testid="mfa-toggle-recovery">
                    {recoveryMode ? "Use authenticator app instead" : "Use a recovery code instead"}
                </button>
            )}
        </form>
    );
}
